import React from "react";
import {
  FaFileAlt,
  FaLock,
  FaPlus,
  FaSearch,
} from "react-icons/fa";

import { safeArray, stripHtml } from "../utils/coffreFortUtils";

import CoffreSegmentEditorPanel from "./CoffreSegmentEditorPanel";

function SegmentSidebarItem({ segment, active, onClick }) {
  const filesCount = safeArray(segment?.fichiers).length;
  const hasContent = !!stripHtml(segment?.segment_content || "");

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full rounded-2xl p-3 text-left transition ${
        active
          ? "bg-[#013E8E] text-white shadow-sm dark:bg-indigo-500"
          : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-800 dark:hover:bg-slate-800"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="line-clamp-1 text-sm font-bold">
            {segment.segment_name || "Segment sans nom"}
          </p>

          <p
            className={`mt-1 text-xs ${
              active ? "text-white/75" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            Ordre #{segment.ordre_segment ?? "—"}
          </p>
        </div>

        <span
          className={`shrink-0 rounded-xl px-2 py-1 text-xs font-bold ${
            active
              ? "bg-white/15 text-white"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
        >
          {filesCount}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
            active
              ? "bg-white/15 text-white"
              : hasContent
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
              : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
          }`}
        >
          {hasContent ? "Contenu" : "Sans contenu"}
        </span>

        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
            active
              ? "bg-white/15 text-white"
              : filesCount
              ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
              : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          {filesCount} fichier(s)
        </span>
      </div>
    </button>
  );
}

function EmptyPanel({ onCreate }) {
  return (
    <section className="flex min-h-[420px] items-center justify-center rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaLock />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun segment sélectionné
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Créez une catégorie pour organiser les documents du coffre-fort.
        </p>

        <button
          type="button"
          onClick={onCreate}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
        >
          <FaPlus />
          Nouveau segment
        </button>
      </div>
    </section>
  );
}

function SkeletonShell() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="grid min-h-[620px] lg:grid-cols-[320px,1fr]">
        <aside className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 lg:border-b-0 lg:border-r">
          <div className="space-y-3">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        </aside>

        <main className="p-5">
          <div className="h-[480px] animate-pulse rounded-[28px] bg-slate-100 dark:bg-slate-800" />
        </main>
      </div>
    </section>
  );
}

export default function CoffreFortSegmentsShell({
  segments,
  filteredSegments,
  activeSegment,
  activeSegmentId,
  setActiveSegmentId,
  search,
  setSearch,
  loading,
  savingSegmentId,
  deletingSegmentId,
  fileForms,
  fileToEditId,
  savingFileId,
  deletingFileId,
  onSegmentChange,
  onSaveSegment,
  onDeleteSegment,
  onFileChange,
  onEditFile,
  onResetFileForm,
  onSaveFile,
  onDeleteFile,
  onCreate,
}) {
  if (loading) return <SkeletonShell />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="grid min-h-[720px] lg:grid-cols-[340px,1fr]">
        <aside className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 lg:border-b-0 lg:border-r">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Coffre-fort
              </p>

              <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
                Catégories de documents
              </h3>

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Sélectionnez une catégorie pour modifier son contenu et ses
                fichiers.
              </p>
            </div>

            <button
              type="button"
              onClick={onCreate}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#013E8E] text-white transition hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
              title="Nouveau segment"
            >
              <FaPlus />
            </button>
          </div>

          <div className="relative mt-4">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher un document..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />

            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span className="rounded-full bg-white px-3 py-1 font-bold ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
              {filteredSegments.length} / {segments.length}
            </span>

            <span className="rounded-full bg-white px-3 py-1 font-bold ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
              <FaFileAlt className="mr-1 inline" />
              fichiers
            </span>
          </div>

          <div className="mt-4 max-h-[680px] space-y-2 overflow-y-auto pr-1">
            {filteredSegments.map((segment) => (
              <SegmentSidebarItem
                key={segment.id}
                segment={segment}
                active={activeSegmentId === segment.id}
                onClick={() => setActiveSegmentId(segment.id)}
              />
            ))}

            {!filteredSegments.length ? (
              <p className="rounded-2xl bg-white p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
                Aucun segment trouvé.
              </p>
            ) : null}
          </div>
        </aside>

        <main className="min-w-0 p-4 sm:p-5 lg:p-6">
          {activeSegment ? (
            <CoffreSegmentEditorPanel
              segment={activeSegment}
              savingSegmentId={savingSegmentId}
              deletingSegmentId={deletingSegmentId}
              fileForm={fileForms[activeSegment.id]}
              fileToEditId={fileToEditId[activeSegment.id]}
              savingFileId={savingFileId}
              deletingFileId={deletingFileId}
              onSegmentChange={onSegmentChange}
              onSaveSegment={onSaveSegment}
              onDeleteSegment={onDeleteSegment}
              onFileChange={onFileChange}
              onEditFile={onEditFile}
              onResetFileForm={onResetFileForm}
              onSaveFile={onSaveFile}
              onDeleteFile={onDeleteFile}
            />
          ) : (
            <EmptyPanel onCreate={onCreate} />
          )}
        </main>
      </div>
    </section>
  );
}